import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../firebaseConfig';

/**
 * useUndeliverableEmails — live index of recipient addresses that hard-bounced.
 *
 * The backend writes one doc per dead address into
 * `World/{userId}/undeliverableEmails/{email}` whenever a Mailer-Daemon /
 * delivery-failure notice is detected (see functions/fetchSmartDomainEmails.js).
 * This is the single source of truth the send gates enforce server-side.
 *
 * Loading it here lets the composer strike through and lock exactly those
 * addresses in the To-picker — robustly and independent of whether the bounce
 * could be matched back onto a specific contact (comma-list position, mixed
 * case, or decision-maker-only addresses all still resolve correctly).
 *
 * Per-address only: a fresh, not-yet-bounced address on the same domain stays
 * fully sendable.
 */

const normalizeAddr = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

export default function useUndeliverableEmails(userId) {
  const [undeliverableSet, setUndeliverableSet] = useState(() => new Set());
  // Map<address, { bounceType: 'hard'|'soft', reason: string }> so the UI can
  // label precisely (both hard and soft bounces block sending).
  const [undeliverableInfo, setUndeliverableInfo] = useState(() => new Map());

  useEffect(() => {
    if (!userId) {
      setUndeliverableSet(new Set());
      setUndeliverableInfo(new Map());
      return undefined;
    }

    const unsub = onSnapshot(
      collection(firestore, `World/${userId}/undeliverableEmails`),
      (snap) => {
        const nextSet = new Set();
        const nextInfo = new Map();
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const addr = normalizeAddr(data.email || docSnap.id);
          if (!addr.includes('@')) return;
          nextSet.add(addr);
          nextInfo.set(addr, {
            bounceType: data.bounceType === 'soft' ? 'soft' : 'hard',
            reason: data.reason || null,
          });
        });
        setUndeliverableSet(nextSet);
        setUndeliverableInfo(nextInfo);
      },
      (err) => console.warn('[useUndeliverableEmails] listener failed:', err?.message),
    );

    return () => unsub();
  }, [userId]);

  const isUndeliverable = (email) => undeliverableSet.has(normalizeAddr(email));
  const bounceTypeOf = (email) => {
    const info = undeliverableInfo.get(normalizeAddr(email));
    return info ? info.bounceType : null;
  };

  return { undeliverableSet, undeliverableInfo, isUndeliverable, bounceTypeOf };
}
